/**
 * @file Documents — what an upload may BE, and what a stored document says about
 * itself (T0.8's seam).
 *
 * ## Why this file exists at all
 *
 * Two callers will decide whether a file is acceptable: the upload route
 * (`apps/api/src/routes/documents.ts`, shipped here) and the file picker a person
 * uses (Phase B, not built). If the browser enforces one allowlist and the API
 * enforces another, the disagreement shows up as a file the picker offered and the
 * server refused, or worse — a file the picker refused that the server would have
 * taken. So the list, the cap and the REASON a file is unacceptable live in one
 * place, as pure data and one pure function, and both callers read them.
 *
 * The mirror of the database side is `document_domain` in
 * `packages/db/migrations/0012_documents.sql`: the enum is the closed set of
 * purposes, and a value that exists in one place and not the other is refused at
 * write time by the enum.
 *
 * ## What is deliberately NOT here
 *
 * * **No retention period.** How long a document is kept is a founder decision
 *   nobody has made; encoding "keep for 7 years" here would be inventing policy.
 * * **No virus scanning.** There is no scanner, no key and no vendor account, and
 *   a field that implies one would be a lie in a DTO.
 * * **No thumbnail sizes**, no image dimensions, no transcoding. Nothing resizes
 *   an upload, so nothing may claim to know what a resized one would look like.
 * * **No presigned-upload helper.** The bytes pass through the API — see the
 *   `FilesProvider` comment in `apps/api/src/platform/types.ts:110`.
 *
 * The kernel discipline applies as everywhere in `packages/shared`: no drizzle, no
 * `apps/api` import, no platform API. This module is data and arithmetic over the
 * two facts a request carries — a media type and a byte count.
 */

// ---------------------------------------------------------------------------
// The domains
// ---------------------------------------------------------------------------

/**
 * What a stored file is FOR.
 *
 * A closed list mirroring the `document_domain` enum rather than free text,
 * because the domain is also the FIRST SEGMENT OF THE OBJECT KEY
 * (`{domain}/{fileId}`): a purpose invented at a call site would produce a key no
 * reader could interpret, and the two would have to be kept in step by hand.
 *
 * `other` is a real member, not a fallback: a landlord attaching something that is
 * none of the three named purposes is an ordinary case, and refusing it would push
 * them to file it under a domain that is wrong.
 */
export const DOCUMENT_DOMAINS = [
  { value: "maintenance", label: "Maintenance" },
  { value: "lease", label: "Lease" },
  { value: "expense", label: "Expense" },
  { value: "other", label: "Other" },
] as const;

export type DocumentDomain = (typeof DOCUMENT_DOMAINS)[number]["value"];

/** Mutable-tuple form, for `z.enum` (the shape `EXPENSE_CATEGORY_VALUES` uses). */
export const DOCUMENT_DOMAIN_VALUES = DOCUMENT_DOMAINS.map(
  (domain) => domain.value,
) as [DocumentDomain, ...DocumentDomain[]];

/**
 * Human labels for the four domains.
 *
 * Here rather than in the SPA for the reason `EXPENSE_CATEGORY_LABELS` is: a
 * picker, a filter and a row badge all render the same words, and
 * `cleaning_and_maintenance`-style raw enum values leaking into a UI is how a list
 * starts disagreeing with itself.
 */
export const DOCUMENT_DOMAIN_LABELS: Readonly<Record<DocumentDomain, string>> =
  Object.fromEntries(
    DOCUMENT_DOMAINS.map((domain) => [domain.value, domain.label]),
  ) as Record<DocumentDomain, string>;

// ---------------------------------------------------------------------------
// What an upload may be
// ---------------------------------------------------------------------------

/**
 * The media types this deployment accepts — and therefore the list a picker's
 * `accept` attribute must be built from.
 *
 * Three types: the two image formats that can actually be inspected for location
 * data, and the one document format a lease or a receipt arrives as. It is an
 * ALLOWLIST, so a type not named here is refused — the default for an unknown
 * media type is "no", because the GPS gate can only inspect what it recognises and
 * a stored object's type is what a browser will trust when it renders it.
 *
 * **`image/webp` is deliberately NOT here, and this is the whole reason the list is
 * two images rather than three.** It was on this list; `exifr` (7.1.3) has no WebP
 * file parser, so every WebP reached the GPS gate, could not be inspected, and was
 * refused under fail-closed. That made this module lie: `describeUploadProblem`
 * — which exists so the route and the picker cannot disagree — answered "fine" for
 * a type the API always rejected, and a picker built from it would have offered a
 * file that could never upload. The accepted list is now what actually works.
 *
 * **Re-entry trigger:** add a WebP decoder (or a parser) and put it back here and
 * in `INSPECTABLE_IMAGE_MIME_TYPES` together. Nothing else needs to change — the
 * gate already reports `unsupported` for a format it has no parser for, so the
 * failure mode of a premature re-add is a refusal, not an unchecked image.
 *
 * Note that `application/pdf` is on this list and is NOT inspected: a PDF has no
 * EXIF container, so the GPS gate records `exif_checked = false` rather than
 * pretending it looked. See `apps/api/src/documents/gps.ts`.
 */
export const ALLOWED_UPLOAD_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
] as const;

export type UploadMimeType = (typeof ALLOWED_UPLOAD_MIME_TYPES)[number];

/**
 * The media types the GPS gate reads metadata out of.
 *
 * A subset of the allowlist by design, and a SEPARATE list because the two answer
 * different questions: `ALLOWED_UPLOAD_MIME_TYPES` is "what may be stored",
 * this is "what has an EXIF container we can read". A PDF is storable and
 * uninspectable, and collapsing the two lists would make "we accept PDFs" and "we
 * read PDF metadata" the same sentence.
 *
 * The two lists agree today. `image/webp` is absent from both, and it is absent
 * from the allowlist *because* it is absent here — an image we cannot inspect must
 * not be storable, so the failure belongs at the gate and not three layers later.
 */
export const INSPECTABLE_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
] as const;

export type ImageMimeType = (typeof INSPECTABLE_IMAGE_MIME_TYPES)[number];

/** Is this media type one the GPS gate should be asked to inspect? */
export function isInspectableImageMime(mime: string): mime is ImageMimeType {
  return (INSPECTABLE_IMAGE_MIME_TYPES as readonly string[]).includes(mime);
}

/**
 * The largest upload this deployment accepts: **10 MiB (10,485,760 bytes)**.
 *
 * The Worker request-body limit is the hard ceiling — Cloudflare refuses a request
 * body over 100 MB before our code runs — and an isolate has ~128 MB of memory, so
 * 10 MiB is nowhere near the platform's edge. That is the point: this number is
 * the one a PERSON is told, and it has to be small enough that buffering a
 * maximum-size upload is unremarkable and large enough that no ordinary file is
 * refused.
 *
 * What it has to cover: a 12-megapixel phone JPEG (typically 3–6 MB) with room to
 * spare, and a scanned lease or receipt PDF (a few hundred KB to a few MB). What
 * it deliberately does not cover: a 48 MP camera JPEG straight off the card
 * (15–25 MB) and a multi-hundred-page scanned bundle. Those are real files, and a
 * founder who wants them raises this number — it is one constant, read by the
 * route's streaming limit, the picker and this function.
 *
 * 10 MiB is 10 * 1024 * 1024, not 10,000,000: the cap is compared against a byte
 * count, and a round decimal number here would make the boundary land somewhere
 * nobody can predict from the file size a phone shows.
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** The cap as a sentence, for a UI that must not re-derive it. */
export const MAX_UPLOAD_LABEL = "10 MB";

/**
 * Normalise a media type for comparison: strip parameters, lowercase, trim.
 *
 * A browser sends `image/jpeg; charset=binary` or `IMAGE/JPEG` for the same file a
 * picker reports as `image/jpeg`, and an allowlist that compared raw header bytes
 * would refuse a perfectly ordinary upload. Parameters are dropped rather than
 * parsed — none of the four allowed types defines a meaningful one — and the part
 * that REMAINS is still held against the allowlist, so
 * `image/svg+xml; charset=utf-8` is refused on `image/svg+xml` and not admitted
 * because its tail happens to look like a charset.
 */
export function normaliseMimeType(mime: string): string {
  const [type] = mime.split(";", 1);
  return (type ?? "").trim().toLowerCase();
}

/**
 * Why this upload is unacceptable, or `null` when it is fine.
 *
 * Pure — two values in, a sentence or nothing out — so the route, the picker and
 * the tests all ask the same question and get the same answer. The ORDER is part
 * of the contract and is the order a person experiences: an empty file is refused
 * before its type is discussed, and a file that is both too big and the wrong type
 * is reported as too big, because resizing nothing will fix a PNG of a lease.
 *
 * The reasons are written to be shown to a person verbatim: they say what is wrong
 * and what the acceptable range is, without naming a vendor or a column.
 *
 * It does NOT check the bytes. Whether the content matches the declared type, and
 * whether an image carries GPS tags, are questions about the bytes —
 * `apps/api/src/documents/gps.ts` answers those, on the server, where a caller
 * cannot skip the answer.
 */
export function describeUploadProblem(upload: {
  readonly mime: string;
  readonly bytes: number;
}): string | null {
  if (upload.bytes <= 0) {
    return "the file is empty — there is nothing to store";
  }
  if (upload.bytes > MAX_UPLOAD_BYTES) {
    return `the file is larger than ${MAX_UPLOAD_LABEL} (${String(MAX_UPLOAD_BYTES)} bytes)`;
  }
  const mime = normaliseMimeType(upload.mime);
  if (!(ALLOWED_UPLOAD_MIME_TYPES as readonly string[]).includes(mime)) {
    return `${mime.length === 0 ? "no content type was given" : `'${mime}' is not an accepted file type`} — accepted types are ${ALLOWED_UPLOAD_MIME_TYPES.join(", ")}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// What a refusal MEANS, in words a person can act on
// ---------------------------------------------------------------------------

/**
 * A sentence for each error code the file routes return, or `null` for a code the
 * kernel does not own.
 *
 * **Why this is here and not in the SPA.** `describeUploadProblem` exists so the
 * route and the picker cannot disagree about what an upload may BE; this is the
 * same discipline for what a refusal MEANS. The upload route's 503 is the one that
 * forced it: `files_unavailable` is "this deployment has no object storage", which
 * is a fact about the DEPLOYMENT — a picker that answered "upload failed" would be
 * telling a landlord to retry something no retry can fix.
 *
 * The codes are the ones the routes actually return (`apps/api/src/routes/
 * documents.ts` for the file routes; the three reference routes add
 * `document_not_found` and `document_domain_mismatch` from
 * `apps/api/src/domain/documents.ts`). A code absent from this table is NOT an
 * error: the caller falls back to the server's own message, which is what a
 * route-specific refusal deserves. `null` says "the server's sentence is the one to
 * show", and it is a deliberate answer rather than a missing entry.
 *
 * `gps_metadata_present` is the sentence this table exists for most of all. The
 * upload refused a photo whose location metadata SURVIVED whatever the client did
 * — the strip failed, or there was none — and calling that "invalid file" would
 * hide the one fact the person needs: the platform will not store where they live.
 */
export function uploadErrorMessage(code: string): string | null {
  switch (code) {
    case "files_unavailable":
      return "File storage is not configured on this deployment, so attachments cannot be uploaded or opened here. Nothing is wrong with your file.";
    case "upload_too_large":
      return `That file is larger than ${MAX_UPLOAD_LABEL}. Choose a smaller one.`;
    case "gps_metadata_present":
      return "This photo still contains location data — the browser could not remove it, so it was not uploaded. Remove the location information (or take a new photo without location services) and try again.";
    case "image_format_unsupported":
      return "This image format cannot be checked for location data by this deployment, so it is refused. Convert it to JPEG or PNG and try again.";
    case "image_unreadable":
      return "This image could not be read, so it could not be checked for location data. It may be corrupt or not the format its name suggests.";
    case "storage_unavailable":
      return "The file could not be stored — file storage answered with an error. Try again in a moment.";
    case "document_bytes_missing":
      return "This attachment's file is missing from storage. The record still names it, but the bytes are gone.";
    case "not_found":
      return "That attachment could not be found. It may belong to another workspace.";
    case "document_not_found":
      return "That attachment could not be found in this workspace, so it was not linked.";
    case "document_domain_mismatch":
      return "That file was uploaded for a different purpose, so it was not linked here. Upload it again for this one.";
    default:
      // Unknown to the kernel: the server's own message is the honest answer, and
      // inventing a sentence here would be a second guess at what it meant.
      return null;
  }
}

// ---------------------------------------------------------------------------
// What the routes return
// ---------------------------------------------------------------------------

/**
 * What `POST /workspaces/:id/documents` answers with — the created document's
 * metadata, and nothing else.
 *
 * Deliberately not the bytes and not a URL: the download is its own authorised,
 * audited request (`GET …/documents/:documentId`), and handing back a link here
 * would create a second way to reach the file that bypasses the audit row.
 */
export interface DocumentUploadDto {
  readonly id: string;
  readonly domain: DocumentDomain;
  readonly mime: string;
  readonly bytes: number;
}

/**
 * A stored document's metadata, as any future list or row renders it.
 *
 * The GET route answers with BYTES, not this shape — a document's whole point is
 * its content — so this is the type for a reader (a maintenance timeline, a lease
 * page, a receipt field) that shows what is attached without fetching it. It
 * carries `exifChecked` because the honest answer to "was this image checked for
 * location data" is a fact, not a constant: true for an inspected image, false for
 * a PDF. It does NOT carry `objectKey`: the storage key is an implementation
 * detail of the seam, and exposing it invites a caller to construct one.
 */
export interface DocumentMetadataDto {
  readonly id: string;
  readonly domain: DocumentDomain;
  readonly mime: string;
  readonly bytes: number;
  readonly exifChecked: boolean;
  readonly createdAt: string;
}
